import { useEffect, useState } from "react";
import { api, formatDate, initials } from "../lib/api";
import { StageBadge } from "../components/StageBadge";
import { AddPhysicianDialog } from "../components/AddPhysicianDialog";
import { ImportCSVDialog } from "../components/ImportCSVDialog";
import { PhysicianDetailSheet } from "../components/PhysicianDetailSheet";
import { ScoreBadge } from "../components/ScoreBadge";
import { ComposeEmailDialog } from "../components/ComposeEmailDialog";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MagnifyingGlass, EnvelopeSimple } from "@phosphor-icons/react";
import { STAGES } from "../lib/constants";

const STAGES_LOCAL_DEPRECATED = [];

export default function Leads() {
  const [physicians, setPhysicians] = useState([]);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [composing, setComposing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (stageFilter !== "all") params.stage = stageFilter;
    if (search) params.search = search;
    const r = await api.get("/physicians", { params });
    setPhysicians(r.data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stageFilter]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  return (
    <div className="p-8" data-testid="leads-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] mb-1">Lead Management</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Physicians</h1>
        </div>
        <div className="flex gap-2">
          <ImportCSVDialog onImported={load} />
          <AddPhysicianDialog onAdded={load} />
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5E66]" />
          <Input data-testid="leads-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, NPI, email, city..." className="rounded-none pl-9" />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger data-testid="stage-filter" className="rounded-none w-52"><SelectValue placeholder="All Stages" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] mb-2">{physicians.length} {physicians.length === 1 ? "result" : "results"}</div>

      {/* Table */}
      <div className="bg-white border border-[#E2E2E5]" data-testid="leads-table">
        <div className="grid grid-cols-[2fr_1.2fr_1fr_1.1fr_1fr_80px_120px_50px] gap-0 border-b border-[#E2E2E5] bg-[#F0F0F2]">
          <Th>Physician</Th><Th>Specialty</Th><Th>Type</Th><Th>Clinic / Location</Th><Th>Contact</Th><Th>Score</Th><Th>Stage</Th><Th></Th>
        </div>
        {loading ? (
          <div className="p-12 text-center font-mono text-xs uppercase tracking-wider text-[#5C5E66]">LOADING...</div>
        ) : physicians.length === 0 ? (
          <div className="p-12 text-center text-[#5C5E66]">
            <div className="text-sm mb-2">No physicians yet</div>
            <div className="text-xs font-mono uppercase tracking-wider">Add manually, import CSV, or use the Finder</div>
          </div>
        ) : physicians.map((p) => (
          <div
            key={p.id}
            data-testid={`lead-row-${p.id}`}
            className="grid grid-cols-[2fr_1.2fr_1fr_1.1fr_1fr_80px_120px_50px] gap-0 border-b border-[#E2E2E5] last:border-0 row-hover items-center"
          >
            <Td onClick={() => setSelected(p.id)} className="cursor-pointer">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#0A0B0F] text-white flex items-center justify-center font-mono font-semibold text-[10px]">{initials(p.full_name)}</div>
                <div className="min-w-0">
                  <div className="font-semibold truncate">{p.full_name}</div>
                  {p.npi && <div className="text-[10px] font-mono text-[#5C5E66] truncate">NPI: {p.npi}</div>}
                </div>
              </div>
            </Td>
            <Td onClick={() => setSelected(p.id)} className="text-sm cursor-pointer">{p.specialty || "—"}</Td>
            <Td onClick={() => setSelected(p.id)} className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] cursor-pointer">{p.practice_type}</Td>
            <Td onClick={() => setSelected(p.id)} className="text-sm cursor-pointer">
              {p.practice_name && <div className="truncate font-medium">{p.practice_name}</div>}
              <div className="text-xs text-[#5C5E66] truncate">{[p.city, p.state].filter(Boolean).join(", ") || "—"} <span className="font-mono">{p.zipcode}</span></div>
            </Td>
            <Td onClick={() => setSelected(p.id)} className="text-xs cursor-pointer">
              {p.email && <div className="truncate">{p.email}</div>}
              {p.phone && <div className="font-mono text-[#5C5E66] truncate">{p.phone}</div>}
              {!p.email && !p.phone && "—"}
            </Td>
            <Td onClick={() => setSelected(p.id)} className="cursor-pointer"><ScoreBadge score={p.lead_score} testid={`lead-score-${p.id}`} /></Td>
            <Td onClick={() => setSelected(p.id)} className="cursor-pointer"><StageBadge stage={p.stage} /></Td>
            <Td>
              <button
                onClick={(e) => { e.stopPropagation(); setComposing(p.id); }}
                data-testid={`compose-${p.id}`}
                title="Compose email"
                className="p-1.5 hover:bg-[#006CFF14] text-[#5C5E66] hover:text-[#006CFF]"
              >
                <EnvelopeSimple size={16} weight="regular" />
              </button>
            </Td>
          </div>
        ))}
      </div>

      <PhysicianDetailSheet
        physicianId={selected}
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
        onUpdated={load}
      />

      <ComposeEmailDialog
        physicianId={composing}
        open={!!composing}
        onOpenChange={(o) => !o && setComposing(null)}
      />
    </div>
  );
}

const Th = ({ children }) => <div className="px-4 py-2.5 text-[10px] font-mono uppercase tracking-wider text-[#5C5E66]">{children}</div>;
const Td = ({ children, className = "", onClick }) => <div onClick={onClick} className={`px-4 py-3 ${className}`}>{children}</div>;
